import { PickingInfo } from '@deck.gl/core';
import { GeoJsonLayer } from '@deck.gl/layers';
import { IZone } from '@helpers/types';
import * as turf from '@turf/turf';
import { hexToRgb } from '@utils/colors';
import { Feature, Polygon } from 'geojson';
import { useCallback, useMemo, useState } from 'react';
import { MapRef } from 'react-map-gl/maplibre';

export const useDeckZones = (
  mapRef: React.RefObject<MapRef | null>,
  zones: Array<IZone> | undefined,
) => {
  const [hoverInfo, setHoverInfo] = useState<PickingInfo | null>(null);

  const focusZone = useCallback(
    (selected: string) => {
      if (!zones || !mapRef.current) return;
      const zone = zones.find((z) => z.name === selected);
      if (zone) {
        // Wrap geometry in a Feature for turf.bbox
        const feature: Feature<Polygon> = {
          type: 'Feature',
          geometry: zone.geometry,
          properties: {},
        };
        const bbox = turf.bbox(feature);
        mapRef.current.fitBounds(
          [
            [bbox[0], bbox[1]],
            [bbox[2], bbox[3]],
          ],
          { padding: 40, duration: 1000 },
        );
      }
    },
    [zones, mapRef],
  );

  const layers = useMemo(() => {
    if (!zones) return [];

    const features: Feature<Polygon>[] = zones.map((zone) => ({
      type: 'Feature',
      geometry: zone.geometry,
      properties: {
        name: zone.name,
        color: zone.color,
      },
    }));

    return [
      new GeoJsonLayer({
        id: 'zones-layer',
        data: features,
        pickable: true,
        stroked: true,
        filled: true,
        lineWidthMinPixels: 1,
        getFillColor: (d: any) => [...hexToRgb(d.properties.color), 13], // ~0.05 opacity
        getLineColor: [100, 100, 100, 255],
        getLineWidth: 1,
        onHover: (info) => {
          setHoverInfo(info);
        },
        autoHighlight: true,
      }),
    ];
  }, [zones]);

  return { layers, hoverInfo, focusZone };
};